import React, { useState, useRef, useEffect } from 'react';

export interface CityOption {
  city: string;
  state: 'Lagos State' | 'Ogun State';
}

export const POPULAR_CITIES: CityOption[] = [
  { city: 'Abeokuta (Central / Ibara)', state: 'Ogun State' },
  { city: 'Abeokuta (Obantoko / Asero)', state: 'Ogun State' },
  { city: 'Abeokuta (Kuto / Ita-Eko)', state: 'Ogun State' },
  { city: 'Abeokuta (Oke-Mosan / Camp)', state: 'Ogun State' },
  { city: 'Ota / Sango-Ota', state: 'Ogun State' },
  { city: 'Mowe / Ibafo', state: 'Ogun State' },
  { city: 'Ijebu-Ode', state: 'Ogun State' },
  { city: 'Sagamu', state: 'Ogun State' },
  { city: 'Ifo', state: 'Ogun State' },
  { city: 'Ilaro', state: 'Ogun State' },
  { city: 'Ago-Iwoye', state: 'Ogun State' },
  // Lagos State Cities & Areas
  { city: 'Ikeja', state: 'Lagos State' },
  { city: 'Lekki / Victoria Island / Ikoyi', state: 'Lagos State' },
  { city: 'Yaba / Surulere', state: 'Lagos State' },
  { city: 'Ajah / Sangotedo', state: 'Lagos State' },
  { city: 'Ikorodu', state: 'Lagos State' },
  { city: 'Alimosho / Egbeda / Igando', state: 'Lagos State' },
  { city: 'Festac / Amuwo-Odofin', state: 'Lagos State' },
  { city: 'Oshodi / Isolo', state: 'Lagos State' },
  { city: 'Agege / Ogba', state: 'Lagos State' },
  { city: 'Maryland / Ojota', state: 'Lagos State' },
  { city: 'Apapa / Costain', state: 'Lagos State' },
  { city: 'Epe', state: 'Lagos State' },
  { city: 'Badagry', state: 'Lagos State' },


];

interface SearchableCitySelectProps {
  value: string;
  onChange: (value: string) => void;
  accentColor?: 'emerald' | 'orange' | 'sky';
  label?: string;
  placeholder?: string;
}

export const SearchableCitySelect: React.FC<SearchableCitySelectProps> = ({
  value,
  onChange,
  accentColor = 'emerald',
  label = 'City / Location',
  placeholder = 'Search or select city...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = POPULAR_CITIES.filter(
    (item) =>
      item.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lagosCities = filteredOptions.filter((item) => item.state === 'Lagos State');
  const ogunCities = filteredOptions.filter((item) => item.state === 'Ogun State');

  const focusBorderClass =
    accentColor === 'orange'
      ? 'focus:border-orange-500'
      : accentColor === 'sky'
      ? 'focus:border-sky-500'
      : 'focus:border-emerald-500';

  const activeBgClass =
    accentColor === 'orange'
      ? 'bg-orange-500/20 text-orange-400'
      : accentColor === 'sky'
      ? 'bg-sky-500/20 text-sky-400'
      : 'bg-emerald-500/20 text-emerald-400';

  const handleSelect = (cityName: string, stateName: string) => {
    const fullValue = `${cityName}, ${stateName}`;
    onChange(fullValue);
    setSearchQuery('');
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
        {label}
      </label>

      {/* Select Toggle Box */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm font-medium cursor-pointer flex items-center justify-between transition-all ${focusBorderClass}`}
      >
        <span className={value ? 'text-white font-semibold' : 'text-slate-500'}>
          {value || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Floating Searchable Dropdown */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden max-h-72 flex flex-col animate-in fade-in zoom-in-95">
          {/* Search Input Field */}
          <div className="p-2.5 border-b border-slate-800 bg-slate-950">
            <div className="relative flex items-center">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Abeokuta, Ikeja, Lekki..."
                className={`w-full pl-9 pr-3 py-2 text-xs bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none ${focusBorderClass}`}
                autoFocus
              />
            </div>
          </div>

          {/* City Options List */}
          <div className="overflow-y-auto p-2 space-y-3 flex-1 scrollbar-thin">
            {ogunCities.length > 0 && (
              <div>
                <div className="px-3 py-1 text-[10px] font-black uppercase tracking-wider text-orange-400/90 bg-orange-950/40 rounded-md mb-1">
                  Ogun State (Abeokuta &amp; environs)
                </div>
                {ogunCities.map((item) => {
                  const fullVal = `${item.city}, ${item.state}`;
                  const isSelected = value === fullVal || value === item.city;
                  return (
                    <button
                      key={item.city}
                      type="button"
                      onClick={() => handleSelect(item.city, item.state)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${isSelected ? activeBgClass : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                    >
                      <span>{item.city}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Ogun</span>
                    </button>
                  );
                })}
              </div>
            )}

            {lagosCities.length > 0 && (
              <div>
                <div className="px-3 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-400/90 bg-emerald-950/40 rounded-md mb-1">
                  Lagos State
                </div>
                {lagosCities.map((item) => {
                  const fullVal = `${item.city}, ${item.state}`;
                  const isSelected = value === fullVal || value === item.city;
                  return (
                    <button
                      key={item.city}
                      type="button"
                      onClick={() => handleSelect(item.city, item.state)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${isSelected ? activeBgClass : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                    >
                      <span>{item.city}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Lagos</span>
                    </button>
                  );
                })}
              </div>
            )}

            {filteredOptions.length === 0 && searchQuery.trim() && (
              <div className="p-3 text-center">
                <p className="text-xs text-slate-400 mb-2">No predefined match for "{searchQuery}"</p>
                <button
                  type="button"
                  onClick={() => {
                    onChange(searchQuery.trim());
                    setIsOpen(false);
                    setSearchQuery('');
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold text-white transition-all ${
                    accentColor === 'orange'
                      ? 'bg-orange-500 hover:bg-orange-600'
                      : accentColor === 'sky'
                      ? 'bg-sky-600 hover:bg-sky-700'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  Use "{searchQuery.trim()}"
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
